import { Injectable } from '@angular/core';

import {
  ColumnMapper,
  ColumnMapperEnum,
  ColumnMapperReading,
  DataSelectOption,
  FeatureFlagsConfigService,
  FieldType,
  IndicationColumn,
  IndicationColumnMapper,
  IndicationReading,
  IndicationsTableInspectionDataType,
} from '@olympus/ngx-shared';
import { ThresholdUsageMode } from 'shared-utils/color-manager';
import { CScanType, UtAxisType, UtCanvasType } from 'shared-utils/views';

export const ThresholdUsageModeMapping: Record<ThresholdUsageMode, string> = {
  [ThresholdUsageMode.ABOVE]: 'Above',
  [ThresholdUsageMode.BELOW]: 'Below',
  [ThresholdUsageMode.INSIDE]: 'Inside',
  [ThresholdUsageMode.OUTSIDE]: 'Outside',
};

@Injectable({
  providedIn: 'root',
})
export class IndicationService {
  private readonly _availableReadings: IndicationReading[] = [
    { id: 1, name: 'scanDataCursor' },
    { id: 2, name: 'indexDataCursor' },
    { id: 3, name: 'cursorScanMMinusR' },
    { id: 4, name: 'cursorIndexMMinusR' },
    { id: 5, name: 'cursorUltrasoundMMinusR' },
    { id: 6, name: 'zoneMaximumValue', isZone: true, zoneId: 0, data: CScanType.AMPLITUDE },
    { id: 7, name: 'zoneMaximumValue', isZone: true, zoneId: 1, condition: (indication): boolean => indication?.getPositions().length === 3 },
    { id: 8, name: 'zoneMinimumValue', isZone: true, zoneId: 0, data: CScanType.POSITION, condition: (indication): boolean => indication?.scanType === UtCanvasType.CSCAN },
    { id: 9, name: 'scanReferenceCursor' },
    { id: 10, name: 'indexReferenceCursor' },
    { id: 11, name: 'ultrasoundReferenceCursor' },
    { id: 12, name: 'indicationArea', isZone: true, zoneId: 0, data: 'scanDataInfo.cScanType', hasThreshold: true },
    { id: 13, name: 'indicationDimension', isZone: true, zoneId: 0, data: 'scanDataInfo.cScanType', dimension: 'length', hasThreshold: true },
    { id: 14, name: 'indicationDimension', isZone: true, zoneId: 0, data: 'scanDataInfo.cScanType', dimension: 'width', hasThreshold: true },
    { id: 15, name: 'zoneMeanValue', isZone: true, zoneId: 5000, data: 'scanDataInfo.cScanType' },
    { id: 16, name: 'zoneStandardDeviation', isZone: true, zoneId: 5000, data: 'scanDataInfo.cScanType' },
    { id: 17, name: 'indicationPosition', isZone: true, zoneId: 0, data: 'scanDataInfo.cScanType', axis: 'scan', hasThreshold: true },
    { id: 18, name: 'indicationPosition', isZone: true, zoneId: 0, data: 'scanDataInfo.cScanType', axis: 'index', hasThreshold: true },
    { id: 19, name: 'signalToNoiseRatio', snrType: 'mainSnr' },
    { id: 20, name: 'signalToNoiseRatioThresholdMode' },
    { id: 21, name: 'signalToNoiseRatioThreshold', snrThresholdType: 'mainThreshold' },
    { id: 22, name: 'zoneMaximumValue', isZone: true, zoneId: 0, data: CScanType.POSITION },
    { id: 23, name: 'signalToNoiseRatioThreshold', snrThresholdType: 'otherThreshold' },
    { id: 24, name: 'signalToNoiseRatio', snrType: 'otherSnr' },
    { id: 25, name: 'measuredThickness', isThickness: true },
    { id: 26, name: 'materialLoss', isThickness: true },
  ];

  private readonly _availableColumns: { [columnName: string]: IndicationColumn } = {
    id: { name: 'id', label: 'ID', type: FieldType.TEXT, placeholder: 'XXX' }, // Always
    quickNotes: { name: 'quickNotes', label: 'Section', type: FieldType.INPUT },
    viewTypeName: { name: 'viewTypeName', label: 'View', type: FieldType.TEXT, placeholder: 'XXXXX' },
    configName: { name: 'configName', label: 'Group', type: FieldType.TEXT, placeholder: 'XXXXX' },
    positionScan: { name: 'positionScan', label: 'Pos. Scan', type: FieldType.TEXT, placeholder: 'XXXXXX.XX' },
    positionIndex: { name: 'positionIndex', label: 'Pos. Index', type: FieldType.TEXT, placeholder: 'XXXXXX.XX' },
    length: { name: 'length', label: 'Length', type: FieldType.TEXT, placeholder: 'XXX.XX' },
    width: { name: 'width', label: 'Width', type: FieldType.TEXT, placeholder: 'XXX.XX' },
    depth: { name: 'depth', label: 'Depth', type: FieldType.TEXT, placeholder: 'XXX.XX' },
    aMaxZ2D: { name: 'aMaxZ2D', label: '% Z(2D) max', type: FieldType.TEXT, placeholder: 'XXX.XX' },
    aMaxZ3D: { name: 'aMaxZ3D', label: '% Z(3D) max', type: FieldType.TEXT, placeholder: 'XXX.XX' },
    pMaxZ2D: { name: 'pMaxZ2D', label: 'Depth Z(2D) max', type: FieldType.TEXT, placeholder: 'XXX.XX' },
    tMinZ: { name: 'tMinZ', label: 'T Z(2D) min', type: FieldType.TEXT, placeholder: 'XXX.XX' },
    measuredThickness: { name: 'measuredThickness', label: 'Thickness', type: FieldType.TEXT, placeholder: 'XXX.XX' },
    materialLoss: { name: 'materialLoss', label: 'Mat. Loss', type: FieldType.TEXT, placeholder: 'XXX.XX' },
    flawType: { name: 'flawType', label: 'Flaw Type', type: FieldType.SELECT, options: this.flawTypeOptions },
    verdict: { name: 'verdict', label: 'Verdict', type: FieldType.SELECT, options: this.verdictOptions },
    details: { name: 'details', label: 'Details', type: FieldType.BUTTON_DETAIL },
    imageButton: { name: 'imageButton', label: 'Image', type: FieldType.BUTTON_IMAGE },
    sr: { name: 'sr', label: 'Scan r' },
    ir: { name: 'ir', label: 'Index r' },
    ur: { name: 'ur', label: 'UT r' },
    zScanStart: { name: 'zScanStart', label: 'Scan Z start' },
    zScanEnd: { name: 'zScanEnd', label: 'Scan Z end' },
    zIndexStart: { name: 'zIndexStart', label: 'Index Z start' },
    zIndexEnd: { name: 'zIndexEnd', label: 'Index Z end' },
    zDepthStart: { name: 'zDepthStart', label: 'UT Z start' },
    zDepthEnd: { name: 'zDepthEnd', label: 'UT Z end' },
    positionLength: { name: 'positionLength', label: 'Scan Z pos' },
    positionWidth: { name: 'positionWidth', label: 'Index Z pos' },
    nzScanStart: { name: 'nzScanStart', label: 'Scan NZ start' },
    nzScanEnd: { name: 'nzScanEnd', label: 'Scan NZ end' },
    nzIndexStart: { name: 'nzIndexStart', label: 'Index NZ start' },
    nzIndexEnd: { name: 'nzIndexEnd', label: 'Index NZ end' },
    snr: { name: 'snr', label: 'SNR', type: FieldType.TEXT, placeholder: 'XXX.XX' },
    thresholdMode: { name: 'thresholdMode', label: 'Threshold Mode', type: FieldType.TEXT, placeholder: 'XXXXX' },
    threshold: { name: 'threshold', label: 'Threshold(s)', type: FieldType.TEXT, placeholder: 'XXX.XX - XXX.XX' },
    areaZ: { name: 'areaZ', label: 'Area Z', type: FieldType.TEXT, placeholder: 'XXX.XX' },
    lengthMaxZ: { name: 'lengthMaxZ', label: 'Length Z max', type: FieldType.TEXT, placeholder: 'XXX.XX' },
    widthMaxZ: { name: 'widthMaxZ', label: 'Width Z max', type: FieldType.TEXT, placeholder: 'XXX.XX' },
    mean: { name: 'mean', label: 'NZ mean', type: FieldType.TEXT, placeholder: 'XXX.XX' },
    stdDev: { name: 'stdDev', label: 'NZ std_dev', type: FieldType.TEXT, placeholder: 'XXX.XX' },
  };

  public columnMapper: IndicationColumnMapper = {
    id: { type: ColumnMapperEnum.CUSTOM, callback: model => model.id.toString() },
    quickNotes: { type: ColumnMapperEnum.CUSTOM, callback: (model, defaultValue) => model.quickNotes || defaultValue },
    flawType: { type: ColumnMapperEnum.CUSTOM, callback: (model, defaultValue) => model.defectType || defaultValue },
    verdict: { type: ColumnMapperEnum.CUSTOM, callback: (model, defaultValue) => model.status || defaultValue },
    image: { type: ColumnMapperEnum.CUSTOM, callback: model => model.imageData },
    viewTypeName: { type: ColumnMapperEnum.SCAN },
    positionScan: { type: ColumnMapperEnum.READING, readingIds: [1] },
    positionIndex: { type: ColumnMapperEnum.READING, readingIds: [2] },
    length: { type: ColumnMapperEnum.READING, readingIds: [3] },
    width: { type: ColumnMapperEnum.READING, readingIds: [4] },
    depth: { type: ColumnMapperEnum.READING, readingIds: [5] },
    aMaxZ2D: { type: ColumnMapperEnum.READING, readingIds: [6] },
    aMaxZ3D: { type: ColumnMapperEnum.READING, readingIds: [7] },
    pMaxZ2D: { type: ColumnMapperEnum.READING, readingIds: [22] },
    tMinZ: { type: ColumnMapperEnum.READING, readingIds: [8] },
    measuredThickness: { type: ColumnMapperEnum.READING, readingIds: [25] },
    materialLoss: { type: ColumnMapperEnum.READING, readingIds: [26] },
    sr: { type: ColumnMapperEnum.READING, readingIds: [9] },
    ir: { type: ColumnMapperEnum.READING, readingIds: [10] },
    ur: { type: ColumnMapperEnum.READING, readingIds: [11] },
    areaZ: { type: ColumnMapperEnum.READING, readingIds: [12] },
    lengthMaxZ: { type: ColumnMapperEnum.READING, readingIds: [13] },
    widthMaxZ: { type: ColumnMapperEnum.READING, readingIds: [14] },
    mean: { type: ColumnMapperEnum.READING, readingIds: [15] },
    stdDev: { type: ColumnMapperEnum.READING, readingIds: [16] },
    positionLength: { type: ColumnMapperEnum.READING, readingIds: [17] },
    positionWidth: { type: ColumnMapperEnum.READING, readingIds: [18] },
    snr: {
      type: ColumnMapperEnum.READING,
      readingIds: [19, 24, 20],
      conditional: {
        reading: 20,
        callback: reading => reading.value === ThresholdUsageMode.INSIDE || reading.value === ThresholdUsageMode.OUTSIDE
          ? [24, 19]
          : [19],
      },
      separator: ' / ',
    },
    thresholdMode: { type: ColumnMapperEnum.READING, readingIds: [20], mapping: ThresholdUsageModeMapping },
    threshold: {
      type: ColumnMapperEnum.READING, readingIds: [23, 21, 20], conditional: {
        reading: 20,
        callback: reading => reading.value === ThresholdUsageMode.INSIDE || reading.value === ThresholdUsageMode.OUTSIDE
          ? [23, 21]
          : [21],
      },
    },
    details: { type: ColumnMapperEnum.BUTTON, id: IndicationsTableInspectionDataType.DETAIL },
    imageButton: { type: ColumnMapperEnum.BUTTON, id: IndicationsTableInspectionDataType.IMAGE },
    zScanStart: { type: ColumnMapperEnum.AXIS, axisType: UtAxisType.SCAN, position: 'start' },
    zScanEnd: { type: ColumnMapperEnum.AXIS, axisType: UtAxisType.SCAN, position: 'end' },
    zIndexStart: { type: ColumnMapperEnum.AXIS, axisType: UtAxisType.INDEX, position: 'start' },
    zIndexEnd: { type: ColumnMapperEnum.AXIS, axisType: UtAxisType.INDEX, position: 'end' },
    zDepthStart: { type: ColumnMapperEnum.AXIS, axisType: UtAxisType.ULTRASOUND, position: 'start' },
    zDepthEnd: { type: ColumnMapperEnum.AXIS, axisType: UtAxisType.ULTRASOUND, position: 'end' },
    nzScanStart: { type: ColumnMapperEnum.AXIS, axisType: UtAxisType.SCAN, position: 'start', isNoise: true },
    nzScanEnd: { type: ColumnMapperEnum.AXIS, axisType: UtAxisType.SCAN, position: 'end', isNoise: true },
    nzIndexStart: { type: ColumnMapperEnum.AXIS, axisType: UtAxisType.INDEX, position: 'start', isNoise: true },
    nzIndexEnd: { type: ColumnMapperEnum.AXIS, axisType: UtAxisType.INDEX, position: 'end', isNoise: true },
  };

  public get verdictOptions(): DataSelectOption[] {
    return [
      { value: '', label: '--', selected: false },
      { value: 'Warning', label: 'Warning', selected: false },
      { value: 'Ok', label: 'OK', selected: false },
      { value: 'Rescan', label: 'Rescan', selected: false },
      { value: 'N/A', label: 'N/A', selected: false },
    ];
  }

  public get flawTypeOptions(): DataSelectOption[] {
    if (!this.indicationFeature) {
      return [];
    }

    return [
      { value: '', label: '--', selected: false },
      ...this.indicationFeature.flawTypes.map(name => ({ value: name, label: name, selected: false })),
    ];
  }

  public get tableColumnHeader(): IndicationColumn[] {
    if (!this.indicationFeature) {
      return [];
    }

    return [
      this._availableColumns.id,
      ...this.indicationFeature.columns.map((column: string) => ({ ...this._availableColumns[column] })),
    ];
  }

  public get tableColumnReading(): IndicationColumn[] {
    if (!this.indicationFeature) {
      return [];
    }

    return [
      this._availableColumns.id,
      ...this.indicationFeature.detailReadings.map((column: string) => ({ ...this._availableColumns[column] })),
    ];
  }

  public get indicationReadings(): IndicationReading[] {
    return [...this.indicationFeature.columns, ...this.indicationFeature.detailReadings]
      .map((column: string) => ({ ...this.columnMapper[column] })) // get mapping for columns
      .filter((mapper: ColumnMapper) => mapper.type === ColumnMapperEnum.READING) // get only reading mapping
      .reduce((ids: number[], current: ColumnMapper) => [...ids, ...(current as ColumnMapperReading).readingIds], []) // flats the readingIds array, gets only readingIds
      .map((id: number) => ({ ...this._availableReadings.find((reading: IndicationReading) => reading.id === id) })); // get available reading from id
  }

  public get reportColumnHeader(): IndicationColumn[] {
    const buttonColumns: string[] = Object.entries(this.columnMapper)
      .filter(([, mapper]: [string, ColumnMapper]) => mapper.type === ColumnMapperEnum.BUTTON)
      .map(([field]: [string, ColumnMapper]) => field);

    const reportColumns: IndicationColumn[] = [
      { name: 'notes', label: 'Notes' },
      { name: 'image', label: 'Image' },
    ];

    const mappedColumns: any[] = [...this.tableColumnHeader, ...this.tableColumnReading, ...reportColumns] // concat all columns, readings and extra columns in same array
      .map(column => ({ name: column.name, label: column.label })) // clean all columns with name and label only
      .filter(column => !buttonColumns.includes(column.name)); // remove unwanted column

    const uniqueColumns: { [field: string]: IndicationColumn } = mappedColumns
      .reduce((previous, current) => ({ ...previous, [current.name]: { ...current } }), {}); // remove duplicates

    return Object.values(uniqueColumns);
  }

  private get indicationFeature(): { columns: string[], detailReadings: string[], flawTypes: string[] } {
    return this._featureFlagService.getFeature('indication');
  }

  constructor(
    private readonly _featureFlagService: FeatureFlagsConfigService,
  ) { }
}
